import React from "react";
import { useNavigate } from "react-router-dom";
import {
  dashIN,
  dashOUT,
  kycIN,
  kycOUT,
  logo,
  logoutIN,
  ordersIN,
  ordersOUT,
  reportIN,
  reportOUT,
  settingIN,
  settingOUT,
  taskIN,
  taskOUT,
  usersIN,
  usersOUT,
  walletIN,
  walletOUT,
} from "../assets/images";

import { useAuth } from "../context/auth-context";

import { AiOutlineCloseCircle } from "react-icons/ai";
import {
  MdOutlineKeyboardArrowRight,
  MdOutlineStorefront,
  MdSwapHoriz,
} from "react-icons/md";
import { HiOutlineTicket, HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { IconType } from "react-icons";

type Variant = "desktop" | "mobile";

type NavItem = { path: string; status: number; label: string };

/** Renders the icon for a row; `active` picks the lit/dim variant. */
type IconRenderer = (active: boolean, variant: Variant) => React.ReactNode;

type NavLink = NavItem & {
  kind: "link";
  icon: IconRenderer;
  badge?: string;
};

type NavGroupEntry = {
  kind: "group";
  key: "customer-txn" | "users" | "kyc";
  label: string;
  items: NavItem[];
  icon: IconRenderer;
  /** Child routes that live under the group but have no row of their own. */
  extraStatuses?: number[];
};

type NavEntry = NavLink | NavGroupEntry;

/** Icons shipped as a lit/dim image pair. */
const imgIcon =
  (on: string, off: string): IconRenderer =>
  (active, variant) =>
    (
      <img
        src={active ? on : off}
        className={variant === "mobile" ? "w-5 h-5 object-contain" : ""}
        alt=""
      />
    );

/** Icons that come from react-icons and are colored by class. */
const vectorIcon =
  (Icon: IconType): IconRenderer =>
  (active, variant) =>
    (
      <Icon
        className={`${variant === "mobile" ? "text-[20px]" : "text-[22px]"} ${
          active ? "text-white" : "text-text_feint"
        }`}
        aria-hidden
      />
    );

/** Grouped under the "Customer Txn" dropdown; order matches the old flat list. */
const CUSTOMER_TXN_ITEMS: NavItem[] = [
  { path: "/orders", status: 8, label: "Orders" },
  { path: "/payouts", status: 81, label: "Payouts" },
  { path: "/giftcards", status: 82, label: "Gift cards" },
  { path: "/bill-payments", status: 83, label: "Bill payments" },
];

/** `/users` is now reached through "All users" — the parent only toggles. */
const USERS_ITEMS: NavItem[] = [
  { path: "/users", status: 5, label: "All users" },
  { path: "/users/new-signups", status: 51, label: "New signups" },
];

/** `/kyc-logs` is now reached through "KYC & KYB" — the parent only toggles. */
const KYC_ITEMS: NavItem[] = [
  { path: "/kyc-logs", status: 13, label: "KYC & KYB" },
  { path: "/kyc-logs/compliance", status: 131, label: "Compliance" },
  { path: "/kyc-logs/rules", status: 132, label: "Rules Manager" },
];

/**
 * Single source of truth for the nav — the desktop rail and the mobile drawer
 * both render from this, so a new destination is added in exactly one place.
 */
const NAV: NavEntry[] = [
  {
    kind: "link",
    path: "/dashboard",
    status: 1,
    label: "Dashboard",
    icon: imgIcon(dashOUT, dashIN),
  },
  {
    kind: "link",
    path: "/wallet",
    status: 2,
    label: "Wallets",
    icon: imgIcon(walletOUT, walletIN),
  },
  {
    kind: "group",
    key: "customer-txn",
    label: "Customer Txn",
    items: CUSTOMER_TXN_ITEMS,
    icon: imgIcon(ordersOUT, ordersIN),
  },
  {
    kind: "link",
    path: "/deex-desk",
    status: 84,
    label: "Deex Desk",
    icon: vectorIcon(MdSwapHoriz),
  },
  {
    kind: "link",
    path: "/merchants",
    status: 85,
    label: "Merchants",
    icon: vectorIcon(MdOutlineStorefront),
  },
  {
    kind: "group",
    key: "users",
    label: "Users",
    items: USERS_ITEMS,
    icon: imgIcon(usersOUT, usersIN),
  },
  {
    kind: "group",
    key: "kyc",
    label: "KYC logs",
    items: KYC_ITEMS,
    icon: imgIcon(kycOUT, kycIN),
    extraStatuses: [133],
  },
  {
    kind: "link",
    path: "/rewards",
    status: 11,
    label: "Rewards",
    icon: imgIcon(taskOUT, taskIN),
  },
  {
    kind: "link",
    path: "/invite-codes",
    status: 12,
    label: "Invite Codes",
    icon: vectorIcon(HiOutlineTicket),
    badge: "New",
  },
  {
    kind: "link",
    path: "/surveys",
    status: 14,
    label: "Surveys",
    icon: vectorIcon(HiOutlineClipboardDocumentList),
    badge: "New",
  },
  {
    kind: "link",
    path: "/reports",
    status: 10,
    label: "Reports",
    icon: imgIcon(reportOUT, reportIN),
  },
  {
    kind: "link",
    path: "/settings",
    status: 7,
    label: "Settings",
    icon: imgIcon(settingOUT, settingIN),
  },
];

const groupStatuses = (group: NavGroupEntry) => [
  ...group.items.map((i) => i.status),
  ...(group.extraStatuses ?? []),
];

/**
 * Row chrome per breakpoint. Desktop keeps the full-bleed row with the right
 * edge marker; mobile uses a compact rounded pill so the drawer reads as a
 * list of touch targets rather than a stack of bars.
 */
const rowClass = (variant: Variant, active: boolean) => {
  if (variant === "desktop") {
    return active
      ? "w-full flex cursor-pointer justify-between transform transition-transform duration-300 h-[60px] px-4 bg-[#1E1E1E] border-r-2 border-[white] items-center"
      : "w-full flex cursor-pointer justify-between h-[60px] px-4 items-center";
  }
  return `w-full flex cursor-pointer justify-between items-center h-[46px] px-3 rounded-xl transition-colors ${
    active ? "bg-white/[0.08]" : "active:bg-white/[0.04]"
  }`;
};

const labelClass = (variant: Variant, active: boolean) =>
  active
    ? "font-semibold text-white"
    : variant === "mobile"
    ? "font-normal text-[#A6A6A6]"
    : "font-normal text-text_feint";

type RowProps = {
  entry: NavLink;
  variant: Variant;
  active: boolean;
  onSelect: (item: NavItem) => void;
};

const NavRow: React.FC<RowProps> = ({ entry, variant, active, onSelect }) => (
  <button
    onClick={() => onSelect(entry)}
    aria-current={active ? "page" : undefined}
    className={rowClass(variant, active)}
  >
    <div
      className={`flex items-center ${
        variant === "mobile"
          ? "gap-3 w-full min-w-0"
          : "gap-2 justify-center pl-4"
      }`}
    >
      <span
        className={
          variant === "mobile"
            ? "w-5 flex justify-center shrink-0"
            : "flex items-center"
        }
      >
        {entry.icon(active, variant)}
      </span>
      <h4
        className={`${labelClass(variant, active)} ${
          variant === "mobile" ? "text-[15px] truncate" : ""
        }`}
      >
        {entry.label}
      </h4>
      {entry.badge && (
        <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide bg-secondary_button/20 text-secondary_button">
          {entry.badge}
        </span>
      )}
    </div>
  </button>
);

type NavGroupProps = {
  entry: NavGroupEntry;
  variant: Variant;
  isOpen: boolean;
  onToggle: () => void;
  /** True when any child route is the current page. */
  parentActive: boolean;
  activeStatus: number;
  onSelect: (item: NavItem) => void;
};

/**
 * Collapsible sidebar group. Clicking the parent only expands/collapses — it
 * never navigates — so every destination is an explicit child.
 */
const NavGroup: React.FC<NavGroupProps> = ({
  entry,
  variant,
  isOpen,
  onToggle,
  parentActive,
  activeStatus,
  onSelect,
}) => (
  <div>
    <button
      onClick={onToggle}
      aria-expanded={isOpen}
      className={rowClass(variant, variant === "mobile" ? false : parentActive)}
    >
      <div
        className={`flex items-center ${
          variant === "mobile"
            ? "gap-3 min-w-0"
            : "gap-2 justify-center pl-4"
        }`}
      >
        <span
          className={
            variant === "mobile"
              ? "w-5 flex justify-center shrink-0"
              : "flex items-center"
          }
        >
          {entry.icon(parentActive, variant)}
        </span>
        <h4
          className={`${labelClass(variant, parentActive)} ${
            variant === "mobile" ? "text-[15px] truncate" : ""
          }`}
        >
          {entry.label}
        </h4>
      </div>
      <span
        className={`ml-2 transition-transform duration-200 ${
          isOpen ? "rotate-90" : ""
        }`}
      >
        <MdOutlineKeyboardArrowRight
          className={`${variant === "mobile" ? "text-xl" : "text-2xl"} ${
            parentActive ? "text-white" : "text-[#8A8A8A]"
          }`}
        />
      </span>
    </button>
    {isOpen && (
      <div
        className={
          variant === "mobile"
            ? "ml-[26px] mt-0.5 mb-1 flex flex-col border-l border-white/10 pl-2"
            : "ml-12 mt-1 flex flex-col gap-1"
        }
      >
        {entry.items.map((item) => {
          const active = activeStatus === item.status;
          if (variant === "mobile") {
            return (
              <button
                key={item.status}
                onClick={() => onSelect(item)}
                aria-current={active ? "page" : undefined}
                className={`text-left h-[40px] px-3 rounded-lg text-[14px] transition-colors ${
                  active
                    ? "bg-white/[0.06] text-white font-medium"
                    : "text-[#8A8A8A] active:bg-white/[0.04]"
                }`}
              >
                {item.label}
              </button>
            );
          }
          return (
            <button
              key={item.status}
              onClick={() => onSelect(item)}
              aria-current={active ? "page" : undefined}
              className={`text-left transition-colors text-[14px] text-white ${
                active
                  ? "w-full flex cursor-pointer justify-between transform transition-transform duration-300 h-[48px] px-4 bg-[#1E1E1E] border-r-2 border-[white] items-center"
                  : "w-full flex cursor-pointer justify-between h-[48px] px-4 items-center"
              }`}
            >
              {item.label}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

const Sidebar = () => {
  const navigate = useNavigate();
  const {
    logout,
    activeStatus,
    isOpened,
    setIsOpened,
    setActiveStatus,
    openNavGroup,
    setOpenNavGroup,
  } = useAuth();

  const closeDrawer = React.useCallback(
    () => setIsOpened(false),
    [setIsOpened]
  );

  /** Lock page scroll behind the drawer and let Esc dismiss it. */
  React.useEffect(() => {
    if (!isOpened) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpened, closeDrawer]);

  /** Accordion: opening a group collapses whichever one was open. */
  const toggleGroup = (group: NavGroupEntry["key"]) =>
    setOpenNavGroup((prev: string | null) => (prev === group ? null : group));

  const goTo = (item: NavItem) => {
    navigate(item.path);
    setActiveStatus(item.status);
  };

  const goToFromDrawer = (item: NavItem) => {
    setActiveStatus(item.status);
    closeDrawer();
    navigate(item.path);
  };

  const renderEntries = (variant: Variant) =>
    NAV.map((entry) => {
      const onSelect = variant === "mobile" ? goToFromDrawer : goTo;
      if (entry.kind === "group") {
        return (
          <NavGroup
            key={entry.key}
            entry={entry}
            variant={variant}
            isOpen={openNavGroup === entry.key}
            onToggle={() => toggleGroup(entry.key)}
            parentActive={groupStatuses(entry).includes(activeStatus)}
            activeStatus={activeStatus}
            onSelect={onSelect}
          />
        );
      }
      return (
        <NavRow
          key={entry.status}
          entry={entry}
          variant={variant}
          active={activeStatus === entry.status}
          onSelect={onSelect}
        />
      );
    });

  return (
    <div className="w-full shrink-0 lgss:w-1/6 lgss:h-full lgss:min-h-0 bg-[#0D0D0D]">
      {/* Desktop rail */}
      <div className="hidden lgss:flex lgss:flex-col h-full min-h-0 overflow-y-auto justify-between pb-10 w-full font-manrope bg-[#0D0D0D] pt-[35px] text-white">
        <div>
          <div className="px-8">
            <img src={logo} className="w-[90px] h-[25px]" alt="logo" />
          </div>
          <div className="mt-12 w-full">{renderEntries("desktop")}</div>
        </div>
        <div>
          <div className="w-10/12 h-[0.5px] mt-28 lg:mt-16 mx-auto bg-text_feint" />
          <div
            onClick={logout}
            className="flex justify-between cursor-pointer mt-4 h-[60px] px-4 items-center"
          >
            <div className="flex gap-2 justify-center items-center pl-4">
              <div>
                <img src={logoutIN} alt="" />
              </div>
              <h4 className="font-semibold text-text_feint">Logout</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div className="lgss:hidden">
        <div
          onClick={closeDrawer}
          aria-hidden
          className={`fixed inset-0 z-[55] bg-black/60 backdrop-blur-[2px] transition-opacity duration-300 ${
            isOpened ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        />
        <aside
          aria-hidden={!isOpened}
          className={`fixed z-[60] top-0 left-0 h-[100dvh] w-[80%] max-w-[300px] flex flex-col font-manrope text-white bg-[#0D0D0D] border-r border-white/[0.06] shadow-2xl transform transition-transform duration-300 ease-out ${
            isOpened ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/[0.06]">
            <img src={logo} className="w-[90px] h-[25px]" alt="logo" />
            <button onClick={closeDrawer} aria-label="Close menu">
              <AiOutlineCloseCircle className="text-[#8A8A8A] text-2xl" />
            </button>
          </div>

          <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-3 flex flex-col gap-0.5">
            {renderEntries("mobile")}
          </nav>

          <div className="border-t border-white/[0.06] px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 h-[46px] px-3 rounded-xl active:bg-white/[0.04] transition-colors"
            >
              <span className="w-5 flex justify-center shrink-0">
                <img src={logoutIN} className="w-5 h-5 object-contain" alt="" />
              </span>
              <h4 className="text-[15px] font-medium text-[#A6A6A6]">Logout</h4>
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Sidebar;
